"use client";

import {
  getDBMessages,
  setDBMessages,
  getDBImages,
} from "@/database/local-api";
import {
  useFetcher,
  cleanParams,
  useFetcherMutation,
  useFetcherInfinite,
} from "@/lib/swr";

// SWR skips focus revalidation when navigator.onLine is false. Message fetches
// handle that via onNetworkError → IndexedDB, so allow revalidation anyway.
const messageSwrOptions = {
  isOnline: () => true,
};

/*
  FETCH: Custom hook to fetch a page of messages, either in a single conversation or multiple
*/
export function useMessages({
  options = {},
  waitUntil = false,
  args = {},
} = {}) {
  const { all, distinct, classroomId, parentId, studentId, limit, page } =
    cleanParams(args);

  const params = {
    ...(all && { all }),
    ...(distinct && { distinct }),
    ...(classroomId && { classroomId }),
    ...(parentId && { parentId }),
    ...(studentId && { studentId }),
    ...(limit && { limit }),
    ...(page && { page }),
  };

  const abortController = new AbortController();

  const response = useFetcher(
    waitUntil === true ? `/message` : null,
    {
      auth: true,
      method: "GET",
      data: params,
      signal: abortController.signal,
      callbacks: {
        onNetworkError: async (err) => {
          console.log(err, "network error in useMessages");
          // fetch from local here...
          const localData = await getDBMessages(params);
          for (const result of localData.results) {
            if (result.parent?.profilePicture) {
              result.parent.profilePicture = await getDBImages(result.parent.profilePicture);
            }
            if (result.staffMember?.profilePicture) {
              result.staffMember.profilePicture = await getDBImages(result.staffMember.profilePicture);
            }
          }
          return localData;
        },
        onSuccess(data) {
          setDBMessages(data?.results ?? []);
          return data;
        }
      },
    },
    {
      ...messageSwrOptions,
      ...options,
      onSuccess(data) {
        console.log(data, "success in useMessages");
        options?.onSuccess && options.onSuccess(data);
      },
      onError(data) {
        console.log(data, "error in useMessages");
        options?.onError && options.onError(data);
      },
    }
  );

  return response;
}

/*
  FETCH: Custom hook to fetch pages of messages, either in a single conversation or multiple
*/
export function useMessagesInfinite({
  options = {},
  waitUntil = false,
  args = {},
} = {}) {
  const { all, distinct, classroomId, parentId, studentId, limit, page } =
    cleanParams(args);

  const params = {
    ...(all && { all }),
    ...(distinct && { distinct }),
    ...(classroomId && { classroomId }),
    ...(parentId && { parentId }),
    ...(studentId && { studentId }),
    ...(limit && { limit }),
    // ...(page && { page }),
  };

  const response = useFetcherInfinite(
    function (pageIndex, previousPageData) {
      if (waitUntil !== true) return null;

      if (previousPageData && !previousPageData?.results?.length) return null; // reached the end
      if (previousPageData && !previousPageData?.meta?.next) return null;

      pageIndex++;
      // if (pageIndex === 0) pageIndex = 1;

      return (
        "/message?" +
        new URLSearchParams({ ...params }).toString() +
        `&page=${pageIndex}`
      );
    },
    {
      auth: true,
      method: "GET",
      data: params,
      callbacks: {
        onNetworkError: async (err, argsData) => {
          console.log(err, "network error in useMessagesInfinite");
          // fetch from local here...
          const localData = await getDBMessages({
            ...params,
            page: argsData?.pageIndex
          });
          for (const result of localData.results) {
            if (result.parent?.profilePicture) {
              result.parent.profilePicture = await getDBImages(result.parent.profilePicture);
            }
            if (result.staffMember?.profilePicture) {
              result.staffMember.profilePicture = await getDBImages(result.staffMember.profilePicture);
            }
          }
          return localData;
        },
        onSuccess: (data) => {
          setDBMessages(data?.results ?? []);
          return data;
        }
      },
    },
    {
      revalidateAll: true,
      ...messageSwrOptions,
      ...options,
      onSuccess(data) {
        console.log(data, "success in useMessagesInfinite");
        options?.onSuccess && options.onSuccess(data);
      },
      onError(data) {
        console.log(data, "error in useMessagesInfinite");
        options?.onError && options.onError(data);
      },
    }
  );
  // Soft pages can appear after mutate(undefined)+revalidate (e.g. classroom
  // change). Treat missing results as [] and drop null entries so renders
  // never read `.id` on undefined.
  const allMessages =
    response.data && response.data.length > 0
      ? response.data.flatMap((page) => page?.results ?? []).filter(Boolean)
      : [];
  const hasNextPage =
    response.data?.[response.data.length - 1]?.meta?.next != null;

  return { ...response, allMessages, hasNextPage };
}

/*
  MUTATION: Custom hook to create a message to a caregiver
*/
export function useMessageMutation({ options = {} } = {}) {
  const response = useFetcherMutation(
    `/message`,
    {
      auth: true,
      method: "POST",
      callbacks: {
        onNetworkError: (err, argsData) => {
          console.log(err, "network error in useMessageMutation");
          throw new Error("You must be online to send a message.");
        },
      },
    },
    {
      ...options,
      onSuccess(data) {
        console.log(data, "success in useMessageMutation");
        options?.onSuccess && options.onSuccess(data?.payload, data?.message);
      },
      onError(data) {
        console.log(data, "error in useMessageMutation");
        options?.onError && options.onError(data);
      },
    }
  );

  return response;
}
